class RRRStickyMobileCart {
  constructor(root) {
    this.root = root;

    this.submitButton = root.querySelector(
      '[data-rrr-sticky-submit]'
    );

    this.buttonText = root.querySelector(
      '[data-rrr-sticky-button-text]'
    );

    this.spinner = root.querySelector(
      '[data-rrr-sticky-spinner]'
    );

    this.currentPrice = root.querySelector(
      '[data-rrr-sticky-current-price]'
    );

    this.comparePrice = root.querySelector(
      '[data-rrr-sticky-compare-price]'
    );

    this.optionValue = root.querySelector(
      '[data-rrr-sticky-option-value]'
    );

    this.image = root.querySelector(
      '.rrr-sticky-mobile-cart__image'
    );

    this.message = root.querySelector(
      '[data-rrr-sticky-message]'
    );

    this.variantDataElement = root.querySelector(
      '[data-rrr-sticky-variant-data]'
    );

    this.mobileQuery = window.matchMedia(
      '(max-width: 749px)'
    );

    this.showAfterMainButton =
      root.dataset.showAfterMainButton === 'true';

    this.defaultButtonText =
      root.dataset.defaultButtonText || 'Add to cart';

    this.soldOutText =
      root.dataset.soldOutText || 'Sold out';

    this.unavailableText =
      root.dataset.unavailableText ||
      'Select an available size';

    this.variants = this.parseVariantData();

    this.productInfo = null;
    this.mainForm = null;
    this.mainAddButton = null;
    this.mainButtonsWrapper = null;

    this.currentVariantId = null;
    this.isVisible = false;
    this.scrollTicking = false;
    this.syncTimer = null;

    if (!this.submitButton) return;

    this.findMainProductElements();
    this.bindEvents();
    this.observeProductChanges();
    this.syncFromMainProduct();
    this.updateVisibility();
  }

  parseVariantData() {
    if (!this.variantDataElement) return {};

    try {
      return JSON.parse(
        this.variantDataElement.textContent
      );
    } catch (error) {
      console.warn(
        'RRR sticky cart variant data could not be read.',
        error
      );

      return {};
    }
  }

  findMainProductElements() {
    this.productInfo =
      document.querySelector(
        'product-info[id^="MainProduct-"]'
      ) ||
      document.querySelector('product-info') ||
      document.querySelector(
        '.product__info-container'
      );

    if (!this.productInfo) return;

    this.mainForm =
      this.productInfo.querySelector(
        'product-form form[action*="/cart/add"]'
      ) ||
      this.productInfo.querySelector(
        'form[action*="/cart/add"]'
      );

    this.mainAddButton =
      this.mainForm?.querySelector('[name="add"]') ||
      this.productInfo.querySelector(
        '.product-form__submit'
      );

    this.mainButtonsWrapper =
      this.mainAddButton?.closest(
        '.product-form__buttons'
      ) ||
      this.mainAddButton;
  }

  bindEvents() {
    this.submitButton.addEventListener(
      'click',
      () => {
        this.handleStickySubmit();
      }
    );

    document.addEventListener(
      'change',
      (event) => {
        if (
          this.productInfo &&
          this.productInfo.contains(event.target)
        ) {
          this.scheduleSync();
        }
      }
    );

    window.addEventListener(
      'scroll',
      () => {
        this.requestVisibilityUpdate();
      },
      {
        passive: true
      }
    );

    window.addEventListener(
      'resize',
      () => {
        this.findMainProductElements();
        this.updateVisibility();
      },
      {
        passive: true
      }
    );

    if (
      typeof this.mobileQuery.addEventListener ===
      'function'
    ) {
      this.mobileQuery.addEventListener(
        'change',
        () => {
          this.updateVisibility();
        }
      );
    }
  }

  observeProductChanges() {
    if (!this.productInfo) return;

    this.productObserver = new MutationObserver(
      () => {
        this.scheduleSync();
      }
    );

    this.productObserver.observe(
      this.productInfo,
      {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
          'value',
          'disabled',
          'class'
        ]
      }
    );
  }

  scheduleSync() {
    window.clearTimeout(this.syncTimer);

    this.syncTimer = window.setTimeout(
      () => {
        this.findMainProductElements();
        this.syncFromMainProduct();
        this.updateVisibility();
      },
      80
    );
  }

  getCurrentVariantId() {
    this.findMainProductElements();

    const variantInput =
      this.mainForm?.querySelector(
        'input[name="id"]'
      );

    if (
      variantInput &&
      variantInput.value
    ) {
      return String(variantInput.value);
    }

    const url = new URL(window.location.href);
    const variantFromUrl =
      url.searchParams.get('variant');

    if (variantFromUrl) {
      return String(variantFromUrl);
    }

    return Object.keys(this.variants)[0] || null;
  }

  syncFromMainProduct() {
    const variantId = this.getCurrentVariantId();

    if (!variantId) {
      this.setUnavailable();
      return;
    }

    const variant = this.variants[variantId];

    if (!variant) {
      this.setUnavailable();
      return;
    }

    this.currentVariantId = variantId;

    if (
      this.currentPrice &&
      variant.price
    ) {
      this.currentPrice.textContent =
        variant.price;
    }

    if (this.comparePrice) {
      if (variant.compareAt) {
        this.comparePrice.textContent =
          variant.compareAt;

        this.comparePrice.hidden = false;
      } else {
        this.comparePrice.textContent = '';
        this.comparePrice.hidden = true;
      }
    }

    if (
      this.optionValue &&
      variant.optionValue
    ) {
      this.optionValue.textContent =
        variant.optionValue;
    }

    if (
      this.image &&
      variant.image
    ) {
      this.image.src = variant.image;
    }

    this.submitButton.disabled =
      !variant.available;

    this.buttonText.textContent =
      variant.available
        ? this.defaultButtonText
        : this.soldOutText;

    this.hideMessage();
  }

  setUnavailable() {
    this.submitButton.disabled = true;

    this.buttonText.textContent =
      this.soldOutText;

    this.showMessage(
      this.unavailableText
    );
  }

  handleStickySubmit() {
    this.findMainProductElements();
    this.syncFromMainProduct();

    const variant =
      this.variants[this.currentVariantId];

    if (
      !variant ||
      !variant.available ||
      !this.mainAddButton ||
      this.mainAddButton.disabled
    ) {
      this.showMessage(
        this.unavailableText
      );

      this.scrollToProductOptions();
      return;
    }

    this.setLoading(true);

    this.mainAddButton.click();

    window.setTimeout(
      () => {
        this.setLoading(false);
      },
      1800
    );
  }

  scrollToProductOptions() {
    const variantPicker =
      this.productInfo?.querySelector(
        'variant-selects, variant-radios, .product-form__input'
      );

    variantPicker?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }

  setLoading(loading) {
    if (!this.spinner) return;

    this.spinner.hidden = !loading;
    this.buttonText.hidden = loading;

    if (loading) {
      this.submitButton.setAttribute(
        'aria-busy',
        'true'
      );
    } else {
      this.submitButton.removeAttribute(
        'aria-busy'
      );

      this.buttonText.hidden = false;
    }
  }

  requestVisibilityUpdate() {
    if (this.scrollTicking) return;

    this.scrollTicking = true;

    window.requestAnimationFrame(
      () => {
        this.updateVisibility();
        this.scrollTicking = false;
      }
    );
  }

  shouldBeVisible() {
    if (!this.mobileQuery.matches) {
      return false;
    }

    if (!this.showAfterMainButton) {
      return window.scrollY > 180;
    }

    this.findMainProductElements();

    if (!this.mainButtonsWrapper) {
      return window.scrollY > 300;
    }

    const buttonRect =
      this.mainButtonsWrapper.getBoundingClientRect();

    return buttonRect.bottom < 0;
  }

  updateVisibility() {
    this.setVisible(
      this.shouldBeVisible()
    );
  }

  setVisible(visible) {
    if (this.isVisible === visible) return;

    this.isVisible = visible;

    this.root.classList.toggle(
      'is-visible',
      visible
    );

    if (visible) {
      const height =
        this.root.getBoundingClientRect().height;

      document.documentElement.style.setProperty(
        '--rrr-sticky-mobile-cart-height',
        `${height}px`
      );

      document.body.classList.add(
        'rrr-sticky-mobile-cart-active'
      );
    } else {
      document.body.classList.remove(
        'rrr-sticky-mobile-cart-active'
      );
    }
  }

  showMessage(text) {
    if (!this.message) return;

    this.message.textContent = text;
    this.message.hidden = false;
  }

  hideMessage() {
    if (!this.message) return;

    this.message.textContent = '';
    this.message.hidden = true;
  }

  destroy() {
    this.productObserver?.disconnect();

    this.root.classList.remove(
      'is-visible'
    );

    document.body.classList.remove(
      'rrr-sticky-mobile-cart-active'
    );
  }
}

function initializeRRRStickyMobileCart(
  container = document
) {
  container
    .querySelectorAll(
      '[data-rrr-sticky-mobile-cart]'
    )
    .forEach((root) => {
      if (
        root.dataset.rrrStickyInitialized ===
        'true'
      ) {
        return;
      }

      root.dataset.rrrStickyInitialized =
        'true';

      root.rrrStickyMobileCart =
        new RRRStickyMobileCart(root);
    });
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initializeRRRStickyMobileCart();
    }
  );
} else {
  initializeRRRStickyMobileCart();
}

document.addEventListener(
  'shopify:section:load',
  (event) => {
    initializeRRRStickyMobileCart(
      event.target
    );
  }
);

document.addEventListener(
  'shopify:section:unload',
  (event) => {
    event.target
      .querySelectorAll(
        '[data-rrr-sticky-mobile-cart]'
      )
      .forEach((root) => {
        root.rrrStickyMobileCart?.destroy();
      });
  }
);